import { useEffect, useRef } from "react";

/**
 * Observe DOM mutations on a target ref and run a debounced callback.
 *
 * @param {React.RefObject} ref - The root DOM element to observe.
 * @param {Function} callback - The function to run after debounceDelay.
 * @param {Array<any>} deps - Dependencies that should trigger re-binding.
 * @param {number} debounceDelay - Optional debounce delay in ms (default: 150ms).
 */
const useDebouncedMutationEffect = (ref, callback, deps = [], debounceDelay = 150) => {
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (!ref?.current) return;
    const root = ref.current;

    const observer = new MutationObserver(() => {

      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        callback(root);
      }, debounceDelay);
    });

    observer.observe(root, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => {
      observer.disconnect();
      clearTimeout(timeoutRef.current);
    };
  }, [ref, callback, debounceDelay, ...deps]);
}

export default useDebouncedMutationEffect;